import { useContext } from "react";
import createGradientElement from "../../helpers/markup";
import Picture from "../helpers/Picture";
import { MarkupContext } from "../../providers/Markup";
import { computeLightOrDark, normalizeHex } from "../../helpers/style";
import themeConfig from "../../../theme.config";
import * as HeroIcons from '@heroicons/react/24/solid'

// ==========================================
// 1. DATA PARSER: Cleans and organizes CMS data

// Mid-tone brand backgrounds that only reach WCAG AA with black text. Neither of
// the usual choices clears 4.5:1 on them — white lands at 3.1:1 / 3.8:1 and the
// nds-90-black body grey at 3.3:1 / 2.8:1 — whereas black reaches 6.8:1 / 5.6:1.
const BLACK_TEXT_BACKGROUNDS = ['#499cbc', '#927aab'];

const DynamicHeroIcon = (props) => {
  const { ...icons } = HeroIcons
  let TheIcon
  if (props.link?.linkIconClass) {
    TheIcon = icons[`${props.link?.linkIconClass}Icon`]
  } else if (props.icon) {
    TheIcon = icons[`${props.icon}Icon`]
  }

  // Safety check: if the icon isn't found, don't crash the app
  if (!TheIcon) return null;

  // FIX: Read props.className, or fallback to your default small sizes
  const sizingClasses = props.className ? props.className : 'h-5 w-5 shrink-0 text-current';

  return (
    <TheIcon
      className={`${sizingClasses} ${props.iconClass ? props.iconClass : ''}`}
      role="none"
      aria-hidden="true"
    />
  )
}

// ==========================================
const parseCardData = (cardItem, getPaletteColor) => {
  const hasImage = !!(cardItem.desktopImage?.src || cardItem.mobileImage?.src);

  // The colour picker sends bare hex ('367aab'), so normalise before comparing or
  // handing it to CSS — an un-prefixed value is not a valid CSS colour.
  const bgColor = normalizeHex(String(cardItem.cardBackgroundColour ?? '').trim().toLowerCase());
  const hasBgColor = !!(bgColor && bgColor !== '#none' && bgColor !== '#ffffff' && bgColor !== '#transparent');

  // bgColor is normalised to lowercase '#rrggbb' above, so match it directly.
  const requiresBlackText = hasBgColor && BLACK_TEXT_BACKGROUNDS.includes(bgColor);

  // Image cards sit on a dark gradient/scrim, so they always take light text.
  // Otherwise let the background's luminance decide, as the other card types do —
  // except on the pinned backgrounds, where black is authoritative either way.
  const isDarkBg = hasImage || (hasBgColor && !requiresBlackText && computeLightOrDark(bgColor, getPaletteColor) === 'dark');

  // Only the two pinned backgrounds opt out of the inherited body grey.
  const lightBgTextColorClass = requiresBlackText ? 'text-black' : 'text-nds-90-black';
  const textColorClass = isDarkBg ? 'text-white' : lightBgTextColorClass;
  const descColorClass = isDarkBg ? 'text-white/90' : lightBgTextColorClass;
  const titleSize = cardItem.useSmallTitle ? 'text-lg' : 'md:text-2xl';

  const cardBgStyle = !hasImage && hasBgColor ? { backgroundColor: bgColor } : {};

  const ctaInfo = cardItem.ctaLinkItem && cardItem.ctaLinkItem[0] ? cardItem.ctaLinkItem[0] : null;
  const hasIcon = !!ctaInfo?.linkIconClass;
  const iconName = ctaInfo?.linkIconClass;
  const iconPosition = ctaInfo?.linkPosition;

  const rawDesc = cardItem.description || '';
  const cleanDesc = rawDesc.replace(/(<([^>]+)>)/gi, "").replace(/&nbsp;/gi, "").trim();
  const hasDescription = cleanDesc.length > 0;

  const hasCtaLink = !!(ctaInfo?.link?.url);
  const hasTitle = !!(cardItem.title && String(cardItem.title).trim() !== '');

  // LOGIC: Is it the stacked mobile / split desktop layout?
  const isSimpleCard = hasCtaLink && hasTitle && !hasImage && !hasDescription;

  // AUTO-DETECT CARD TYPE
  let cardType = 'CONTENT';
  if (hasImage) cardType = 'IMAGE';
  else if (isSimpleCard) cardType = 'QUICK_LINK';

  return {
    cardItem, hasImage, hasBgColor, textColorClass, descColorClass, titleSize, cardBgStyle,
    ctaInfo, hasIcon, iconName, iconPosition, hasDescription, isSimpleCard, hasCtaLink, cardType
  };
};

// ==========================================
// 2. SHARED WRAPPER: Handles <a> vs <div> and shadows
// ==========================================
const CardWrapper = ({ data, gridClasses, children }) => {
  const { cardBgStyle, hasImage, hasBgColor, hasCtaLink, ctaInfo, textColorClass } = data;
  
  const baseClasses = `relative overflow-hidden rounded-sm ${!hasImage && !hasBgColor ? 'bg-white' : ''} shadow-2xl shadow-${themeConfig.settings.hero.card.shadow.base}`;

  if (hasCtaLink) {
    return (
      <a
        href={ctaInfo.link.url}
        target={ctaInfo.link.target}
        style={cardBgStyle}
        className={`block aspect-square ${gridClasses} ${baseClasses} ${textColorClass} hover:scale-[1.05] transition-all duration-300 hover:shadow-${themeConfig.settings.hero.card.shadow.hover}/50 rounded-s-3xl rounded-br-3xl`}
      >
        {children}
      </a>
    );
  }

  return (
    <div style={cardBgStyle} className={`flex flex-col aspect-square ${gridClasses} ${baseClasses} rounded-s-3xl rounded-br-3xl`}>
      {children}
    </div>
  );
};

// ==========================================
// 3. UI VARIATION: Image Card
// ==========================================
const ImageCardUI = ({ data }) => {
  const { cardItem, textColorClass, descColorClass, titleSize, hasIcon, iconName, iconPosition, hasDescription } = data;
  const { createMarkup } = useContext(MarkupContext)
  
  return (
    <>
      <div className="absolute inset-0 z-0 overflow-hidden [&_picture]:block [&_picture]:size-full">
        <Picture
          desktopImage={cardItem.desktopImage}
          mobileImage={cardItem.mobileImage}
          className="size-full object-cover"
        />
        {cardItem.imageGradientColour && cardItem.imageGradientType && cardItem.imageGradientType !== "None" 
          ? createGradientElement({
              type: cardItem.imageGradientType,
              fromColor: cardItem.imageGradientColour,
              fullMobile: true,
              classes: 'absolute inset-0 pointer-events-none'
            })
          : <div className="absolute inset-0 bg-black/40 pointer-events-none" aria-hidden="true"></div>
        }
      </div>

      <div className={`absolute inset-0 z-10 flex flex-col justify-center p-3 md:p-4 ${cardItem.titleAlignment === 'Center' ? 'text-center' : 'text-left'}`}>
        {cardItem.title && (
          <div className={`font-bold flex items-center gap-1 leading-tight ${cardItem.titleAlignment === 'Center' ? 'justify-center' : 'justify-start'} ${titleSize} ${textColorClass}`}>
            {hasIcon && (!iconPosition || String(iconPosition).toLowerCase() === 'before') && (
              <DynamicHeroIcon icon={iconName} iconClass={iconName} className="w-5 h-5 shrink-0 text-current" />
            )}
            <span className="min-w-0 break-words">{cardItem.title}</span>
            {hasIcon && String(iconPosition).toLowerCase() === 'after' && (
              <DynamicHeroIcon icon={iconName} iconClass={iconName} className="w-5 h-5 shrink-0 text-current" />
            )}
          </div>
        )}
        {hasDescription && (
          <div className={`mt-1 line-clamp-2 ${descColorClass}`} data-rte="true" dangerouslySetInnerHTML={createMarkup(cardItem.description)} />
        )}
      </div>
    </>
  );
};

// ==========================================
// 4. UI VARIATION: Quick Link Card (Stacked Mobile / Split Desktop)
// ==========================================
const QuickLinkCardUI = ({ data }) => {
  const { cardItem, textColorClass, titleSize, hasIcon, iconName } = data;
  
  return (
    <div 
      className={`max-md:relative absolute inset-0 z-10 flex max-md:flex-row max-md:justify-start max-md:gap-4 flex-col justify-between md:justify-center md:p-4
      max-md:items-center max-md:py-6 max-md:px-5
      ${cardItem.titleAlignment === 'Center' ? 'text-center max-md:text-left' : 'text-left'}`}
    >
      {hasIcon && (
        <div className={`max-md:mb-0 mb-2 flex ${cardItem.titleAlignment === 'Center' ? 'justify-center max-md:justify-start' : 'justify-start'}`}>
          <DynamicHeroIcon icon={iconName} iconClass={iconName} className={`max-md:w-6 max-md:h-6 w-10 h-10 md:w-12 md:h-12 shrink-0 ${textColorClass}`} />
        </div>
      )}
      
      {cardItem.title && (
        <div className={`font-bold flex items-center gap-1 leading-tight ${cardItem.titleAlignment === 'Center' ? 'justify-center max-md:justify-start' : 'justify-start'} ${titleSize} ${textColorClass} ${!hasIcon ? 'mt-0' : ''}`}>
          <span className="min-w-0 break-words">{cardItem.title}</span>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 5. UI VARIATION: Standard Content Card
// ==========================================
const ContentCardUI = ({ data }) => {
  const { cardItem, textColorClass, descColorClass, titleSize, hasIcon, iconName, hasDescription } = data;
  const { createMarkup } = useContext(MarkupContext)

  return (
    <div className={`absolute inset-0 z-10 flex flex-col justify-center p-3 md:p-4 ${cardItem.titleAlignment === 'Center' ? 'text-center' : 'text-left'}`}>
      {hasIcon && (
        <div className={`mb-2 flex ${cardItem.titleAlignment === 'Center' ? 'justify-center' : 'justify-start'}`}>
          <DynamicHeroIcon icon={iconName} iconClass={iconName} className={`w-10 h-10 md:w-12 md:h-12 shrink-0 ${textColorClass}`} />
        </div>
      )}
      {cardItem.title && (
        <div className={`font-bold flex items-center gap-1 leading-tight ${cardItem.titleAlignment === 'Center' ? 'justify-center' : 'justify-start'} ${titleSize} ${textColorClass}`}>
          <span className="min-w-0 break-words">{cardItem.title}</span>
        </div>
      )}
      {hasDescription && (
        <div className={`mt-1  line-clamp-2 ${descColorClass}`} data-rte="true" dangerouslySetInnerHTML={createMarkup(cardItem.description)} />
      )}
    </div>
  );
};

// ==========================================
// 6. MAIN EXPORT: The Smart Router
// ==========================================
export default function HeroCard({ cardItem, index, cardStartCols, cardSpanCols }) {
  const { getPaletteColor } = useContext(MarkupContext)
  const data = parseCardData(cardItem, getPaletteColor);
  
  // Base grid column layout math
  const baseGridClasses = `${index === 0 ? cardStartCols : ''} w-full cardItem md:col-span-4 2xl:col-span-4 ${cardSpanCols}`;

  if (data.cardType === 'IMAGE') {
    return (
      <CardWrapper data={data} gridClasses={`max-md:!col-span-2 ${baseGridClasses}`}>
        <ImageCardUI data={data} />
      </CardWrapper>
    );
  }

  if (data.cardType === 'QUICK_LINK') {
    return (
      // Notice: Removes aspect-square and stretches full width on mobile!
      <CardWrapper data={data} gridClasses={`max-md:!col-span-4 max-md:!aspect-auto ${baseGridClasses}`}>
        <QuickLinkCardUI data={data} />
      </CardWrapper>
    );
  }

  // Default fallback for CONTENT cards and plain background squares
  return (
    <CardWrapper data={data} gridClasses={`max-md:!col-span-2 ${baseGridClasses}`}>
      <ContentCardUI data={data} />
    </CardWrapper>
  );
}